/**
 * Welcome Film — the first thing a child ever sees in Gurukul Veda.
 *
 * Five hand-painted scenes, a Study Buddy walking into each one, captions that
 * land one line at a time, a Ken-Burns drift on every backdrop and a progress
 * ribbon along the top. Everything is in-app (framer-motion + generated
 * artwork), so there is no file to upload and nothing to buffer.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import confetti from 'canvas-confetti';
import { ChevronLeft, ChevronRight, Pause, Play, SkipForward, Sparkles } from 'lucide-react';

const SCENES = [
  {
    key: 'dawn',
    bg: 'https://static.prod-images.emergentagent.com/jobs/eb175c5d-51a0-4ace-b433-822f579519a6/images/38a76707e1d5fd2866e5924dffb4b0f6a9911d7acbd4ca30a56088ed76a82479.jpeg',
    buddies: ['chirpy.png'],
    title: 'Welcome to Gurukul Veda',
    lines: [
      'A quiet little corner of the internet that belongs only to you.',
      'No bells. No marks on the board. Nobody watching.',
    ],
    ink: 'text-amber-50',
    glow: 'from-amber-900/80',
    ms: 6200,
  },
  {
    key: 'stage',
    bg: 'https://static.prod-images.emergentagent.com/jobs/eb175c5d-51a0-4ace-b433-822f579519a6/images/457f35519346f4cbdcf1e0da0bb5405c0f30afc591dd760b230544c89716be64.jpeg',
    buddies: ['chirpy.png', 'bhalu.jpg', 'saathi.png', 'unico.png', 'wolfy.jpg'],
    title: 'Meet your Study Buddies',
    lines: [
      'Chirpy, Bhalu, Saathi, Unico, Wolfy and friends.',
      'Pick one and they stay with you — every chapter, every single day.',
    ],
    ink: 'text-emerald-50',
    glow: 'from-teal-950/80',
    ms: 7000,
  },
  {
    key: 'questions',
    bg: 'https://static.prod-images.emergentagent.com/jobs/eb175c5d-51a0-4ace-b433-822f579519a6/images/0139b4f0a4591b7df50805f61fc14604d5395c0c832ead0e436145cac59181f2.jpeg',
    buddies: ['wolfy.jpg'],
    title: 'Ask absolutely anything',
    lines: [
      'Black holes. Dinosaurs. That sum you got wrong in class today.',
      'Your buddy never judges, never rushes, and never tells anyone.',
    ],
    ink: 'text-indigo-50',
    glow: 'from-indigo-950/85',
    ms: 6600,
  },
  {
    key: 'treasure',
    bg: 'https://static.prod-images.emergentagent.com/jobs/eb175c5d-51a0-4ace-b433-822f579519a6/images/85c3da6e9d5f367ad12725f2a48d21b156eca419cd58c468f7c460a5cc309dfa.jpeg',
    buddies: ['unico.png'],
    title: 'Fill your treasure chest',
    lines: [
      'Solve puzzles, invite a friend, share a chapter from your book.',
      'Veda Coins pile up — and buy you themes, hints and free months.',
    ],
    ink: 'text-amber-50',
    glow: 'from-amber-950/85',
    ms: 6600,
    burst: true,
  },
  {
    key: 'journey',
    bg: 'https://static.prod-images.emergentagent.com/jobs/eb175c5d-51a0-4ace-b433-822f579519a6/images/0ae4bca061347286883dd8826482e014e8c42fd99a942f2f36ee9484eb76e9ae.jpeg',
    buddies: ['saathi.png', 'drago.jpg'],
    title: 'This bit is your adventure',
    lines: [
      'Five quick questions and your Gurukul is ready.',
      'Two minutes. Then we start learning together.',
    ],
    ink: 'text-emerald-50',
    glow: 'from-emerald-950/80',
    ms: 7000,
  },
];

export function WelcomeFilm({ open, studentName = '', onDone }) {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);
  const scene = SCENES[i];
  const isLast = i === SCENES.length - 1;

  const next = useCallback(() => {
    setI(prev => (prev < SCENES.length - 1 ? prev + 1 : prev));
  }, []);

  useEffect(() => {
    if (!open || paused || isLast) return undefined;
    timer.current = setTimeout(next, scene.ms);
    return () => clearTimeout(timer.current);
  }, [open, paused, i, isLast, next, scene.ms]);

  useEffect(() => {
    if (!open || !scene.burst) return;
    const t = setTimeout(() => {
      confetti({
        particleCount: 70, spread: 90, startVelocity: 34, gravity: 0.7, ticks: 180,
        origin: { x: 0.5, y: 0.62 },
        colors: ['#FCD34D', '#F59E0B', '#FDE68A', '#FFFFFF'],
        disableForReducedMotion: true,
      });
    }, 900);
    return () => clearTimeout(t);
  }, [open, i, scene.burst]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[90] bg-black overflow-hidden" data-testid="welcome-film">
      {/* Progress ribbon */}
      <div className="absolute top-0 left-0 right-0 z-30 flex gap-1.5 px-3 pt-3">
        {SCENES.map((s, idx) => (
          <div key={s.key} className="h-1 flex-1 rounded-full bg-white/25 overflow-hidden">
            <motion.div
              className="h-full bg-white rounded-full"
              initial={{ width: idx < i ? '100%' : '0%' }}
              animate={{ width: idx < i ? '100%' : idx === i ? '100%' : '0%' }}
              transition={{ duration: idx === i && !paused && !isLast ? scene.ms / 1000 : 0.2, ease: 'linear' }}
            />
          </div>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={scene.key}
          className="absolute inset-0 overflow-hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.7 }}
        >
          {/* Backdrop with a slow cinematic drift */}
          <motion.img
            src={scene.bg}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            initial={{ scale: 1.12, x: i % 2 ? 14 : -14 }}
            animate={{ scale: 1.0, x: 0 }}
            transition={{ duration: (scene.ms + 2200) / 1000, ease: 'linear' }}
          />
          <div className={`absolute inset-0 bg-gradient-to-t ${scene.glow} via-black/35 to-transparent`} />

          {/* Buddies walking in */}
          <div className="absolute inset-x-0 bottom-[30%] sm:bottom-[26%] flex items-end justify-center gap-2 sm:gap-5 px-4">
            {scene.buddies.map((file, bi) => (
              <motion.img
                key={file}
                src={`/buddies/${file}`}
                alt=""
                className="w-16 h-16 sm:w-24 sm:h-24 rounded-full object-cover ring-4 ring-white/70 shadow-2xl"
                initial={{ y: 60, opacity: 0, rotate: bi % 2 ? 8 : -8 }}
                animate={{ y: [0, -9, 0], opacity: 1, rotate: 0 }}
                transition={{
                  y: { duration: 2.4, repeat: Infinity, ease: 'easeInOut', delay: 0.35 + bi * 0.12 },
                  opacity: { duration: 0.6, delay: 0.25 + bi * 0.12 },
                  rotate: { duration: 0.7, delay: 0.25 + bi * 0.12 },
                }}
              />
            ))}
          </div>

          {/* Captions */}
          <div className="absolute inset-x-0 bottom-0 px-6 sm:px-14 pb-24 sm:pb-28">
            <div className="max-w-2xl">
              <motion.p
                className="inline-flex items-center gap-1.5 text-[10px] sm:text-[11px] font-heading uppercase tracking-[0.3em] text-white/70"
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
              >
                <Sparkles className="w-3 h-3" /> Scene {i + 1} of {SCENES.length}
              </motion.p>
              <motion.h2
                className={`font-heading font-extrabold ${scene.ink} text-3xl sm:text-5xl leading-[1.05] mt-2 drop-shadow-[0_4px_18px_rgba(0,0,0,0.55)]`}
                initial={{ opacity: 0, y: 26, filter: 'blur(6px)' }}
                animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                transition={{ duration: 0.65, delay: 0.3 }}
                data-testid={`film-title-${scene.key}`}
              >
                {isLast && studentName ? `${studentName}, this bit is your adventure` : scene.title}
              </motion.h2>
              {scene.lines.map((line, li) => (
                <motion.p
                  key={line}
                  className="text-white/85 font-body text-sm sm:text-lg mt-2 max-w-xl drop-shadow-[0_2px_10px_rgba(0,0,0,0.6)]"
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.75 + li * 0.5 }}
                >
                  {line}
                </motion.p>
              ))}
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Controls */}
      <div className="absolute inset-x-0 bottom-0 z-30 flex items-center justify-between gap-3 px-4 sm:px-6 py-4 bg-gradient-to-t from-black/70 to-transparent">
        <div className="flex items-center gap-2">
          <button data-testid="film-prev" aria-label="Previous scene" disabled={i === 0}
            onClick={() => setI(p => Math.max(0, p - 1))}
            className="w-9 h-9 rounded-full bg-white/15 text-white flex items-center justify-center backdrop-blur hover:bg-white/25 disabled:opacity-30 transition-colors">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button data-testid="film-pause" aria-label={paused ? 'Play' : 'Pause'}
            onClick={() => setPaused(p => !p)}
            className="w-9 h-9 rounded-full bg-white/15 text-white flex items-center justify-center backdrop-blur hover:bg-white/25 transition-colors">
            {paused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
          </button>
          <button data-testid="film-next" aria-label="Next scene" disabled={isLast}
            onClick={next}
            className="w-9 h-9 rounded-full bg-white/15 text-white flex items-center justify-center backdrop-blur hover:bg-white/25 disabled:opacity-30 transition-colors">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        <div className="flex items-center gap-2">
          {!isLast && (
            <button data-testid="film-skip" onClick={onDone}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-white/80 text-xs font-heading hover:text-white hover:bg-white/10 transition-colors">
              <SkipForward className="w-3.5 h-3.5" /> Skip
            </button>
          )}
          <motion.button data-testid="film-start" onClick={onDone}
            animate={isLast ? { scale: [1, 1.05, 1] } : { scale: 1 }}
            transition={isLast ? { duration: 1.6, repeat: Infinity } : { duration: 0.2 }}
            className="px-5 sm:px-7 py-2.5 rounded-full bg-gradient-to-r from-amber-400 via-orange-400 to-rose-400 text-slate-900 text-sm font-heading font-bold shadow-xl shadow-orange-500/30 hover:brightness-110 transition-all">
            {isLast ? "Let's set up my Gurukul" : 'Start now'}
          </motion.button>
        </div>
      </div>
    </div>
  );
}

export default WelcomeFilm;
